package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class Absence(
    val id: String,
    val userId: String,
    val workplaceId: String?,
    val type: AbsenceType,
    val status: AbsenceStatus,
    val startDate: String,
    val endDate: String,
    val reason: String?,
    val attachmentUrl: String?,
    val processedBy: String?,
    val processedAt: String?,
    val metadata: AuditMetadata,
    /**
     * SH-57: sick leave paid in full (PL: 100%, e.g. pregnancy or an accident on the way to work,
     * as the sick note says) instead of the usual 80%. Only the rate is kept, never the cause.
     */
    val sickPayFull: Boolean = false,
    /** SH-164: why a manager rejected the request (optional), shown to the employee. */
    val rejectionReason: String? = null,
    /**
     * SH-223: part of one day only ("HH:mm", both set, [startDate] == [endDate]): childcare or
     * force majeure taken in hours, time off in lieu of overtime. Null: whole days.
     */
    val startTime: String? = null,
    val endTime: String? = null,
    /** SH-223: the occasion of an [AbsenceType.OCCASIONAL_LEAVE] (sets its days). */
    val occasion: OccasionReason? = null,
    /** SH-223: whom an [AbsenceType.CARE_ALLOWANCE] is for (the ZUS limits differ). */
    val careFor: CareRecipient? = null,
    /**
     * SH-223: days or hours, for the types whose yearly limit is either ([AbsenceType.CHILDCARE],
     * [AbsenceType.FORCE_MAJEURE]); the first request of the year sets it for the year.
     */
    val limitUnit: LimitUnit? = null,
    /** SH-223: the number of children born at once (maternity and parental leave hints). */
    val children: Int? = null,
    /**
     * SH-223: the overtime hours an [AbsenceType.TIME_OFF_IN_LIEU] gives back: its own hours when
     * the employee asked (1:1), two thirds of them when the employer granted it unasked (1.5:1,
     * art. 151² § 2 KP).
     */
    val compensatedOvertimeHours: Double? = null,
    /** SH-223: the manager's note when approving over a yearly limit. */
    val overrideNote: String? = null
) {
    /** SH-223: only part of a day. */
    val isHourly: Boolean get() = !startTime.isNullOrBlank() && !endTime.isNullOrBlank()
}

/**
 * What an absence request is for. The first four are what apps before SH-223 know; the others are
 * stored with the nearest of those as `type` and the precise one as `kind` (AbsenceMapper), so an
 * older app keeps reading them (and blocks the days). See docs/requests.md.
 */
@Serializable
enum class AbsenceType {
    SICK_LEAVE,
    VACATION,
    ON_DEMAND,
    FORCE_MAJEURE,
    /** Urlop okolicznościowy (§ 15 of the 1996 regulation): a wedding, a birth, a funeral. */
    OCCASIONAL_LEAVE,
    /** Krwiodawstwo: the day of the donation and the next (ustawa o publicznej służbie krwi, art. 9). */
    BLOOD_DONATION,
    /** Czas wolny za nadgodziny (art. 151² KP). */
    TIME_OFF_IN_LIEU,
    /** Opieka nad dzieckiem do 14 lat (art. 188 KP): 2 days or 16 hours a year. */
    CHILDCARE,
    /** Urlop opiekuńczy (art. 173¹ KP): 5 unpaid days a year. */
    CARER_LEAVE,
    /** Zasiłek opiekuńczy (ZUS): care of a sick child or relative, or a child whose nursery or school closed. */
    CARE_ALLOWANCE,
    /** Urlop bezpłatny (art. 174 KP). */
    UNPAID_LEAVE,
    /** Urlop macierzyński (art. 180 KP). */
    MATERNITY,
    /** Urlop ojcowski (art. 182³ KP). */
    PATERNITY,
    /** Urlop rodzicielski (art. 182¹a KP). */
    PARENTAL,
    /** Urlop wychowawczy (art. 186 KP). */
    CHILDCARE_LEAVE,
    /** Kwarantanna lub izolacja: paid as sickness (ustawa zasiłkowa art. 6 ust. 2). */
    QUARANTINE;

    companion object {
        /** Tolerant lookup: null for a name this version does not know. */
        fun fromName(name: String?): AbsenceType? = entries.firstOrNull { it.name == name }
    }
}

/** SH-223: the occasions of urlop okolicznościowy (§ 15 of the MPiPS regulation of 15 May 1996). */
@Serializable
enum class OccasionReason(val days: Int) {
    /** The employee's own wedding. */
    OWN_WEDDING(2),
    /** The birth of the employee's child. */
    CHILD_BIRTH(2),
    /** Death and funeral of a spouse, child, father, mother, stepfather or stepmother. */
    DEATH_CLOSE_FAMILY(2),
    /** The wedding of the employee's child. */
    CHILD_WEDDING(1),
    /** Death and funeral of a sibling, grandparent, parent-in-law or someone the employee supports or cares for. */
    DEATH_OTHER_RELATIVE(1)
}

/** SH-223: whom a care allowance is for: a child up to 14 (or 8, a closed nursery or school) or another family member. */
@Serializable
enum class CareRecipient { CHILD, FAMILY }

/** SH-223: how a yearly limit is counted. */
@Serializable
enum class LimitUnit { DAYS, HOURS }

@Serializable
enum class AbsenceStatus {
    PENDING,
    APPROVED,
    REJECTED
}

// domain/model/dashboard/EmployeeDashboard.kt

@Serializable
data class EmployeeDashboard(
    val nextShift: NextShiftHighlight?, // Najbliższa zmiana (lub null)
    val monthSummary: MyMonthSummary,
    val marketplaceOpportunites: Int, // "3 zmiany dostępne do wzięcia!"
    val myNotifications: Int // Nieprzeczytane powiadomienia
)

@Serializable
data class NextShiftHighlight(
    val shiftId: String,
    val startTime: String, // "Jutro, 08:00"
    val role: String,      // "Barista"
    val locationName: String, // "Aurora Gdańsk"
    val timeUntil: String  // "Za 14 godzin"
)

@Serializable
data class MyMonthSummary(
    val totalHours: Double,
    val estimatedEarnings: Double, // Ukryte, jeśli user nie ma dostępu do stawek
    val shiftsCount: Int
)
